import type {ReactElement} from 'react';
import {useState} from 'react';
import type {Editor} from 'grapesjs';
import {AiRedesignPromptBox} from '@Components/ai-resdesign-prompt-box';
import type {
  AiRedesignPromptBoxProps,
  EditableComponentsMap,
  PromptOptionsForEditableComponents,
  SeasonalPromptPill,
  SelectedPromptOptionValues,
} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {EditableComponent, PromptOption, PromptSuggestions} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {
  EmailCampaignAiRedesignSource,
  EmailCampaignAiRewriteSource,
  getAiRedesignCost,
  useGetRemainingCreditsForEmailCampaignAiContent,
} from '@Libraries/ai-email-campaign-content-library';
import {useAppSelector} from '@/hooks';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {Text, TextSize} from '@Components/text';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from '@Components/email-maker-ai-redesign-prompt-box/email-maker-ai-redesign-prompt-box.module.scss';
import {AiButton} from '@Components/ai-button';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {Size} from '@Components/button';
import {openAiRedesignPromptBoxModal} from '@Modals/ai-redesign-prompt-box-modal';
import {getEmailTextWithCcid, isMobileWidth} from '@Libraries/email-campaigns-library';
import {getRedesignIssueIconFromCode, getRedesignIssueTypeFromCode} from '@Libraries/email-campaign-ai-redesign-library';
import {AiTextErrorTypes} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {getErrorMessageFromCode} from '@Libraries/ai-text-prompt-box-library';
import {AiRedesignPromptBoxIssue} from '@Libraries/ai-redesign-prompt-box-library';
import {EMAIL_MAKER_AI_REDESIGN_SINGUP_SOURCE} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {isChristmasSeason, SeasonalDecorationEmoji} from '@Utils/seasonal-promotion.util';

export interface EmailMakerAiRedesignPromptBoxProps {
  editor: Editor;
  showInitialAnimation: boolean;
  recentPrompt?: string;
}

export function EmailMakerAiRedesignPromptBox({editor, ...props}: EmailMakerAiRedesignPromptBoxProps): ReactElement {
  const isMobile = isMobileWidth();

  const getEditableComponentsMap = (): EditableComponentsMap => {
    return {
      [EditableComponent.TEXT]: true,
      [EditableComponent.IMAGE]: true,
    };
  };

  const getPromptOptionsForEditableComponents = (): PromptOptionsForEditableComponents => {
    return {
      [EditableComponent.TEXT]: [PromptOption.MOOD, PromptOption.EMOJI_USAGE],
      [EditableComponent.IMAGE]: [PromptOption.MOOD],
    };
  };

  const remainingCredits = useGetRemainingCreditsForEmailCampaignAiContent();
  const initialEditableComponentsMap = getEditableComponentsMap();
  const initialRedesignCost = getAiRedesignCost(initialEditableComponentsMap);

  const [prompt, setPrompt] = useState<undefined | string>(undefined);
  const [lastPrompt, setLastPrompt] = useState<string | undefined>(props.recentPrompt);
  const [redesignCost, setRedesignCost] = useState<number>(initialRedesignCost);
  const isPromptBoxLoading = useAppSelector((state) => state.emailMakerAiRedesignPromptBox.isLoading);

  const recalculateAiRedesignCost = (editableComponentsMap: EditableComponentsMap): void => {
    const updatedCost = getAiRedesignCost(editableComponentsMap);
    setRedesignCost(updatedCost);
  };

  const redesignCampaign = async (prompt: string, selectedPromptOptionValues: SelectedPromptOptionValues, editableComponentsMap?: EditableComponentsMap): Promise<void> => {
    if (!editableComponentsMap) {
      return;
    }

    const textComponents = getEmailTextWithCcid(editor, EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REDESIGN);
    const images = editor.getWrapper()?.find(`[data-pmw-type="${window.PMW.emails.components.graphic.PMW_IMAGE}"]`) ?? [];
    const backgroundImages = editor.getWrapper()?.find('[style*="background-image:url"], [style*="background-image: url"]') ?? [];
    const designPosters = editor.getWrapper()?.find(`[data-pmw-type="${window.PMW.emails.components.graphic.PMW_DESIGN}"]`) ?? [];

    const hasText = textComponents.length > 0;
    const hasImages = images.length + backgroundImages.length + designPosters.length > 0;

    const allModificationEnabled = editableComponentsMap[EditableComponent.TEXT] && editableComponentsMap[EditableComponent.IMAGE];

    if (allModificationEnabled && !hasText && !hasImages) {
      const message = window.i18next.t(getErrorMessageFromCode(AiTextErrorTypes.NoTextField));
      const icon = getRedesignIssueIconFromCode(AiTextErrorTypes.NoTextField);
      const type = getRedesignIssueTypeFromCode(AiTextErrorTypes.NoTextField);
      throw new AiRedesignPromptBoxIssue(message, icon, type);
    }

    if (allModificationEnabled && !hasText) {
      editableComponentsMap.text = false;
    }

    if (allModificationEnabled && !hasImages) {
      editableComponentsMap.image = false;
    }

    await editor.runCommand(window.PMW.emails.commands.AI_REDESIGN_EMAIL_CAMPAIGN, {
      prompt: prompt,
      selectedPromptOptionValues: selectedPromptOptionValues,
      editableComponentsMap: editableComponentsMap,
      usageSource: EmailCampaignAiRedesignSource.EMAIL_MAKER_AI_REDESIGN,
    });
    setLastPrompt(prompt);
  };

  const getAiPromptBoxProps = (): AiRedesignPromptBoxProps => {
    return {
      showInitialAnimation: props.showInitialAnimation,
      regenerateContent: redesignCampaign,
      defaultPrompt: prompt,
      onPromptChange: setPrompt,
      editableComponentsMap: initialEditableComponentsMap,
      promptOptionsForEditableComponents: getPromptOptionsForEditableComponents(),
      onEditableComponentsMapChange: recalculateAiRedesignCost,
      promptSuggestions: PromptSuggestions.DEFAULT,
      isPromptBoxLoading: isPromptBoxLoading,
      recentPrompt: lastPrompt,
      creditsPillContent: {
        remainingCredits: remainingCredits,
        actionCost: redesignCost,
      },
      upsellDialog: {
        dialogName: window.PMW.PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGN_AI_REDESIGN as string,
        showDialog: !isUserPremium(),
      },
      placeholder: window.i18next.t('pmwjs_redesign_this_for_my_coffee_shop_ellipsis'),
      authenticateModalContent: {
        showModal: !isUserLoggedIn(),
        modalProps: {
          fbPermissions: '',
          signupSource: EMAIL_MAKER_AI_REDESIGN_SINGUP_SOURCE,
          onLoginSuccess: async (): Promise<void> => {
            await editor.runCommand(window.PMW.emails.commands.SAVE_TO_DB_AND_CACHE);
            window.location.reload();
          },
        },
      },
      seasonalPromptPill: getSeasonalPromptPill(),
    };
  };

  const getSeasonalPromptPill = (): SeasonalPromptPill | undefined => {
    if (isChristmasSeason()) {
      return {
        label: `${SeasonalDecorationEmoji.CHRISTMAS} ${window.i18next.t('pmwjs_christmas')}`,
        prompts: [
          window.i18next.t('pmwjs_redesign_for_christmas_party_invite'),
          window.i18next.t('pmwjs_turn_this_into_a_christmas_sale_announcement'),
          window.i18next.t('pmwjs_redesign_email_with_christmas_theme'),
          window.i18next.t('pmwjs_make_this_email_festive_with_christmas_visuals'),
          window.i18next.t('pmwjs_redesign_this_email_to_a_christmas_newsletter'),
        ],
        labelClasses: styles.christmasPromptPillLabel,
      } as SeasonalPromptPill;
    }

    return undefined;
  };

  const openPromptBoxModal = (): void => {
    openAiRedesignPromptBoxModal({
      title: window.i18next.t('pmwjs_ai_redesign'),
      icon: 'icon-sparkle',
      isPromptBoxSelectedOnRender: true,
      promptBoxClasses: styles.mobilePromptBox,
      ...getAiPromptBoxProps(),
    });
  };

  const getPromptBoxToggler = (): ReactElement => {
    return (
      <div className={'flexbox flex-align-items-center flex-justify-between'}>
        <div className={'flexbox flex-align-items-center gap-2'}>
          <Text val={'AI Redesign'} size={TextSize.XSMALL} className={`content-ai`} />
          <PulsatingDot containerClassname={`_position-relative  ${styles.pulsatingDot}`} />
        </div>
        <AiButton
          variant={AIButtonVariant.SUBDUED}
          size={Size.MEDIUM}
          icon={'icon-sparkle'}
          onClick={openPromptBoxModal}
          isLoading={isPromptBoxLoading}
          customClasses={styles.headerButton}
        />
      </div>
    );
  };

  const getAiRedesignPromptBox = (): ReactElement => {
    if (isMobile) {
      return getPromptBoxToggler();
    }

    return (
      <AiRedesignPromptBox
        {...getAiPromptBoxProps()}
        unselectOnOutsideClick
        promptBoxClasses={styles.promptBox}
        headerContent={{
          title: window.i18next.t('pmwjs_ai_redesign'),
          icon: 'icon-sparkle',
          showPulsatingDot: true,
        }}
      />
    );
  };

  return getAiRedesignPromptBox();
}
